import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardContent, Typography, Box, Grid, Chip, Avatar, Rating } from "@mui/material";
import { Star, TrendingUp, Schedule, AttachMoney, People } from "@mui/icons-material";
import {
  fetchConsultantPerformanceApi,
  fetchConsultantEarningsApi,
  fetchSessionAnalyticsApi,
} from "../../services/enhancedDashboard";
import { toastMessage } from "../../utils/toastMessage";

const ConsultantPerformanceCharts = ({ data, startDate, endDate }) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({
    performanceMetrics: {
      series: [
        { name: "Response Time (min)", data: [] },
        { name: "Completion Rate (%)", data: [] },
        { name: "User Rating", data: [] },
        { name: "Earnings (₹)", data: [] },
      ],
      options: {
        chart: { type: "radar", height: 350 },
        xaxis: { categories: [] },
        yaxis: { min: 0, max: 100 },
        title: { text: "Consultant Performance Overview", align: "left" },
        colors: ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0"],
        legend: { position: "top" },
      },
    },
    topPerformers: {
      series: [{ name: "Earnings", data: [] }],
      options: {
        chart: { type: "bar", height: 300 },
        plotOptions: {
          bar: { horizontal: true, columnWidth: "70%" },
        },
        dataLabels: { enabled: false },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Earnings (₹)" } },
        title: { text: "Top Earning Consultants", align: "left" },
        colors: ["#4caf50"],
      },
    },
    consultantRatings: {
      series: [],
      options: {
        chart: { type: "donut", height: 300 },
        labels: ["5 Stars", "4 Stars", "3 Stars", "2 Stars", "1 Star"],
        colors: ["#4caf50", "#8bc34a", "#ff9800", "#ff5722", "#f44336"],
        legend: { position: "bottom" },
        title: { text: "Consultant Rating Distribution", align: "left" },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%";
          },
        },
      },
    },
    availabilityChart: {
      series: [{ name: "Available Hours", data: [] }],
      options: {
        chart: { type: "heatmap", height: 300 },
        dataLabels: { enabled: false },
        xaxis: { categories: [] },
        yaxis: { categories: [] },
        title: { text: "Consultant Availability Heatmap", align: "left" },
        colors: ["#00acc1"],
      },
    },
    earningsTrend: {
      series: [{ name: "Total Earnings", data: [] }],
      options: {
        chart: { type: "area", height: 300 },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
          },
        },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Earnings (₹)" } },
        title: { text: "Consultant Earnings Trend", align: "left" },
        colors: ["#ff9800"],
      },
    },
    sessionTypes: {
      series: [],
      options: {
        chart: { type: "pie", height: 300 },
        labels: ["Chat", "Audio Call", "Video Call", "Group Session"],
        colors: ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0"],
        legend: { position: "bottom" },
        title: { text: "Session Types Distribution", align: "left" },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%";
          },
        },
      },
    },
  });

  // Sample data - replace with actual API data
  useEffect(() => {
    const sampleData = {
      performanceMetrics: {
        series: [
          { name: "Response Time (min)", data: [15, 20, 18, 12, 25, 22, 16] },
          { name: "Completion Rate (%)", data: [95, 88, 92, 98, 85, 90, 94] },
          { name: "User Rating", data: [4.5, 4.2, 4.7, 4.8, 4.1, 4.3, 4.6] },
          { name: "Earnings (₹)", data: [45000, 38000, 52000, 48000, 35000, 42000, 46000] },
        ],
        options: {
          ...chartData.performanceMetrics.options,
          xaxis: { categories: ["Dr. Smith", "Dr. Johnson", "Dr. Brown", "Dr. Davis", "Dr. Wilson", "Dr. Miller", "Dr. Garcia"] },
        },
      },
      topPerformers: {
        series: [{ name: "Earnings", data: [65000, 58000, 52000, 48000, 45000, 42000, 38000] }],
        options: {
          ...chartData.topPerformers.options,
          xaxis: { categories: ["Dr. Smith", "Dr. Johnson", "Dr. Brown", "Dr. Davis", "Dr. Wilson", "Dr. Miller", "Dr. Garcia"] },
        },
      },
      consultantRatings: {
        series: [45, 30, 15, 7, 3],
        options: chartData.consultantRatings.options,
      },
      availabilityChart: {
        series: [
          { name: "Mon", data: [8, 7, 6, 8, 7, 5, 4] },
          { name: "Tue", data: [7, 8, 7, 6, 8, 6, 5] },
          { name: "Wed", data: [6, 7, 8, 7, 6, 7, 6] },
          { name: "Thu", data: [8, 6, 7, 8, 7, 6, 5] },
          { name: "Fri", data: [7, 8, 6, 7, 8, 5, 4] },
          { name: "Sat", data: [5, 6, 7, 6, 5, 8, 7] },
          { name: "Sun", data: [4, 5, 6, 5, 4, 7, 8] },
        ],
        options: {
          ...chartData.availabilityChart.options,
          xaxis: { categories: ["6AM", "9AM", "12PM", "3PM", "6PM", "9PM", "12AM"] },
          yaxis: { categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] },
        },
      },
      earningsTrend: {
        series: [{ name: "Total Earnings", data: [120000, 135000, 150000, 165000, 180000, 195000, 210000] }],
        options: {
          ...chartData.earningsTrend.options,
          xaxis: { categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"] },
        },
      },
      sessionTypes: {
        series: [40, 30, 20, 10],
        options: chartData.sessionTypes.options,
      },
    };

    setChartData(sampleData);
  }, [data]);

  const consultantStats = [
    {
      title: "Total Consultants",
      value: "45",
      change: "+3 this month",
      trend: "up",
      icon: <People />,
      color: "#1976d2",
    },
    {
      title: "Avg. Rating",
      value: "4.6",
      change: "+0.2",
      trend: "up",
      icon: <Star />,
      color: "#ff9800",
    },
    {
      title: "Avg. Response Time",
      value: "18 min",
      change: "-3 min",
      trend: "up",
      icon: <Schedule />,
      color: "#2e7d32",
    },
    {
      title: "Total Earnings",
      value: "₹2.1M",
      change: "+15%",
      trend: "up",
      icon: <AttachMoney />,
      color: "#4caf50",
    },
  ];

  const topConsultants = [
    { name: "Dr. Sarah Smith", rating: 4.9, earnings: 65000, sessions: 120, avatar: "SS" },
    { name: "Dr. John Johnson", rating: 4.8, earnings: 58000, sessions: 110, avatar: "JJ" },
    { name: "Dr. Emily Brown", rating: 4.7, earnings: 52000, sessions: 95, avatar: "EB" },
    { name: "Dr. Michael Davis", rating: 4.6, earnings: 48000, sessions: 88, avatar: "MD" },
    { name: "Dr. Lisa Wilson", rating: 4.5, earnings: 45000, sessions: 82, avatar: "LW" },
  ];

  return (
    <Box>
      {/* Consultant Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {consultantStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="h2" color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Chip
                      label={stat.change}
                      size="small"
                      color={stat.trend === "up" ? "success" : "error"}
                      icon={stat.icon}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Box color={stat.color} fontSize="2rem">
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Top Consultants */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Performing Consultants
          </Typography>
          <Grid container spacing={2}>
            {topConsultants.map((consultant, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: "#1976d2", mr: 2 }}>
                        {consultant.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{consultant.name}</Typography>
                        <Box display="flex" alignItems="center">
                          <Rating value={consultant.rating} precision={0.1} readOnly size="small" />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {consultant.rating}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="textSecondary">
                        Earnings: ₹{consultant.earnings.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Sessions: {consultant.sessions}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Consultant Performance Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.performanceMetrics.options}
                series={chartData.performanceMetrics.series}
                type="radar"
                height={350}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.topPerformers.options}
                series={chartData.topPerformers.series}
                type="bar"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.consultantRatings.options}
                series={chartData.consultantRatings.series}
                type="donut"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.sessionTypes.options}
                series={chartData.sessionTypes.series}
                type="pie"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.earningsTrend.options}
                series={chartData.earningsTrend.series}
                type="area"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Availability Heatmap
              </Typography>
              <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Heatmap visualization would go here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConsultantPerformanceCharts;

